import React from 'react';
import { get } from 'lodash-es';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useCallback from '@/util/hook/useCallback';
import useDispatch from '@/util/hook/useDispatch';
import useEffect from '@/util/hook/useEffect';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { selectLiffId } from '@/util/redux/auth';
import { handleSkipCurrentPage } from '../../util/helper/handleSkipCurrentPage';
import { updateCareClientProfile } from '../../util/helper/profileUpdate';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { selectCareClientVisitorProfile, } from '../../redux/module/careClientVisitorProfile';
import { selectMarketingChannelList } from '../../redux/module/remoteConfig';
import { selectIsCareClientVisitorProfileInitialized } from '../../redux/module/tracker';
import View from './View';
import { APP } from '../../config/app';
import { MARKETING_CHANNEL_OTHER_OPTION_TITLE } from '../../config/option';
const getProcessedMarketingChannelList = ({ selectedMarketingChannelList, otherChannel, }) => {
    // 1. remove MARKETING_CHANNEL_OTHER_OPTION_TITLE from the list
    const processedMarketingChannelList = selectedMarketingChannelList.filter((channel) => channel !== MARKETING_CHANNEL_OTHER_OPTION_TITLE);
    // 2. add otherChannel to the list if it is not empty
    const trimmedOtherChannel = otherChannel.trim();
    if (trimmedOtherChannel) {
        processedMarketingChannelList.push(trimmedOtherChannel);
    }
    // 3. make sure the list does not contain empty string
    return processedMarketingChannelList.filter((channel) => channel !== '');
};
const useSkipCurrentPage = ({ isCareClientVisitorProfileInitialized, careClientVisitorProfile, fromPage, currentPage, navigate, }) => {
    useEffect(() => {
        if (!isCareClientVisitorProfileInitialized)
            return;
        if (fromPage === null)
            return;
        const marketingChannelList = get(careClientVisitorProfile, 'marketingChannelList', []);
        const shouldSkip = marketingChannelList.length > 0;
        if (!shouldSkip)
            return;
        handleSkipCurrentPage({
            fromPage,
            currentPage,
            navigate,
            nextPage: APP.ROUTES.CONTACT_INFO,
            prevPage: null,
        });
    }, [isCareClientVisitorProfileInitialized, fromPage]);
};
const useResetOtherChannel = ({ selectedMarketingChannelList, setOtherChannel, }) => {
    useEffect(() => {
        if (!selectedMarketingChannelList.includes(MARKETING_CHANNEL_OTHER_OPTION_TITLE)) {
            setOtherChannel('');
        }
    }, [selectedMarketingChannelList]);
};
export default function Controller() {
    const { isLoading, setLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const [selectedMarketingChannelList, setSelectedMarketingChannelList] = useState([]);
    const [otherChannel, setOtherChannel] = useState('');
    const [isOtherChannelError, setIsOtherChannelError] = useState(false);
    const _id = useSelector(selectLiffId);
    const marketingChannelOptionList = useSelector(selectMarketingChannelList);
    const isCareClientVisitorProfileInitialized = useSelector(selectIsCareClientVisitorProfileInitialized);
    const careClientVisitorProfile = useSelector(selectCareClientVisitorProfile);
    const dispatch = useDispatch();
    const { navigate, navigateError, useLocation } = useRouter();
    const location = useLocation();
    const currentPage = location.pathname;
    const { fromPage } = useTrackBrowserHistory();
    useSkipCurrentPage({
        isCareClientVisitorProfileInitialized,
        careClientVisitorProfile,
        fromPage,
        currentPage,
        navigate,
    });
    useResetOtherChannel({
        selectedMarketingChannelList,
        setOtherChannel,
    });
    useGetCareClientVisitorProfile({
        setApiProcessingPool,
        setApiDonePool,
    });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    // event handlers
    const handleOptionChipClick = useCallback(({ channel }) => {
        setSelectedMarketingChannelList((prev) => prev.includes(channel) ? prev.filter((item) => item !== channel) : [...prev, channel]);
    }, []);
    const handleOtherChannelTextareaClick = useCallback((e) => {
        // prevent triggering the onClick event on option chip
        e.stopPropagation();
        setIsOtherChannelError(false);
    }, []);
    const handleOtherChannelTextareaChange = useCallback((e) => {
        setOtherChannel(e.target.value);
    }, []);
    const handleForwardButtonClick = async () => {
        const isOtherChannelUnfinished = selectedMarketingChannelList.includes(MARKETING_CHANNEL_OTHER_OPTION_TITLE) && !otherChannel;
        if (isOtherChannelUnfinished) {
            setIsOtherChannelError(true);
        }
        else {
            const processedMarketingChannelList = getProcessedMarketingChannelList({
                selectedMarketingChannelList,
                otherChannel,
            });
            setLoading(true);
            const { isSuccess } = await updateCareClientProfile({
                _id,
                updatedData: {
                    marketingChannelList: processedMarketingChannelList,
                },
                dispatch,
                navigateError,
            });
            if (isSuccess) {
                navigate(APP.ROUTES.CONTACT_INFO);
            }
            setLoading(false);
        }
    };
    const didCompleteRequiredFormFields = selectedMarketingChannelList.length > 0;
    return (<View 
    // data
    isLoading={isLoading} marketingChannelOptionList={marketingChannelOptionList} selectedMarketingChannelList={selectedMarketingChannelList} otherChannel={otherChannel} isOtherChannelError={isOtherChannelError} isForwardButtonAvailable={didCompleteRequiredFormFields} 
    // func
    handleOptionChipClick={handleOptionChipClick} handleOtherChannelTextareaClick={handleOtherChannelTextareaClick} handleOtherChannelTextareaChange={handleOtherChannelTextareaChange} handleForwardButtonClick={handleForwardButtonClick}/>);
}
